/* eslint-disable max-lines */
import { BaseComponent, IStateBase } from 'components/BaseComponent';
import Error from 'components/Error';
import { enParameters } from 'enums/enParameters';
import { IPaymentResult } from 'interfaces/IPaymentResult';
import { enLang, IProductConfig, IProductData } from 'interfaces/IProductData';
import get from 'lodash/get';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Observable } from 'rxjs/Observable';
import { captchaService } from 'services/CaptchaService';
import { cartService } from 'services/CartService';
import { langService } from 'services/LangService';
import { parameterService } from 'services/ParameterService';
import { paymentService } from 'services/PaymentService';
import { enLegacyPixelPath, legacyPixelService } from 'services/LegacyPixelService';
import { WithRouter } from 'shared/decorators/withRouter';
import { HistoryContext } from 'shared/HistoryContext';
import { showError } from 'shared/services/dialog';
import { Cookies } from 'react-cookie';

import HotsiteTheme from './Templates/Hotsite';
import MobileTheme from './Templates/Mobile';
import WidgetTheme from './Templates/Widget';
import goofyService from 'services/GoofyService';
import { loadTimeService } from '../../services/LoadTimeService';
import { GOOFY_ID } from 'settings';
import { paymentProgressService } from '../../services/PaymentProgressService';
import { enPaymentStatus } from 'enums/enPaymentStatus';
import { translateService } from 'services/TranslateService';
import { enPaymentMethod } from 'enums/enPaymentMethod';
import { enPixelPath, pixelService } from 'services/PixelService';
import { WithStyles } from 'shared/decorators/withStyles';
import { currencyService } from 'services/CurrencyService';
import { Timer } from './Timer';
import { configService } from 'services/ConfigService';
import { enPaymentType } from 'enums/enPaymentType';

export interface ITemplateState extends IStateBase {
  cart: IProductData;
  config: IProductConfig;
}

interface IState extends IStateBase, ITemplateState {
  loading: boolean;
  config: IProductConfig;
  hasTimer: boolean;
}

export interface IPayProps {
  onPay: () => Observable<boolean>;
}

interface IProps {}

let attempts = 0;
const payAttempts = {};

@WithRouter()
@WithStyles(() => ({
  component: {
    '& .cart-container': {
      margin: '0 auto'
    },
    '& .cart-container-scarcity-timer': {
      paddingTop: 65
    }
  }
}))
export default class CartPage extends BaseComponent<IState, IProps> {
  constructor(props: any) {
    super(props);

    this.state = {
      loading: true,
      cart: null,
      config: null,
      hasTimer: false
    };
  }

  componentDidMount() {
    parameterService
      .get(enParameters.lang)
      .take(1)
      .filter(lang => lang !== null)
      .bindComponent(this)
      .subscribe((lang: enLang) => {
        langService.setLang(lang);
      });

    Observable.combineLatest(Observable.of(this.props.match.params.id), currencyService.getCurrency())
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      .switchMap(([id, currency]) => {
        return cartService
          .loadContent(id, currency)
          .loader()
          .catch(err => showError(err));
      })
      .bindComponent(this)
      .subscribe(() => {
        this.setState({
          loading: false
        });
      });

    // Goofy para envio de dados do usuário
    paymentService
      .getCart()
      .filter(cart => !!(cart.name && cart.phone && cart.email))
      .debounceTime(5000)
      .map(cart => {
        return {
          name: cart.name,
          phone: cart.phone,
          email: cart.email
        };
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      .bindComponent(this)
      .subscribe(data => {
        // set user data
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'user_data',
          data: {
            name: data.name,
            email: data.email,
            phone: data.phone
          },
          subtract: 5000
        });
      });

    // Goofy para envio de dados de endereço
    cartService
      .getCart()
      .filter(cart => !!(cart.config.askAddressBeforePayment || cart.config.askAddressAfterPayment))
      .switchMap(() => paymentService.getCart())
      .filter(cart => !!cart.address)
      .debounceTime(2000)
      .map(cart => {
        return {
          address: {
            postalCode: cart.address.postalCode,
            street: cart.address.street,
            number: cart.address.number,
            district: cart.address.district,
            complement: cart.address.complement,
            province: cart.address.province,
            city: cart.address.city,
            cityId: cart.address.cityId,
            country: cart.address.country
          }
        };
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      .bindComponent(this)
      .subscribe(data => {
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'address_data',
          data,
          subtract: 5000
        });
      });

    // Goofy rendered
    cartService
      .getCart()
      .first()
      .filter(() => {
        const path = window.location.pathname;
        return !!path.match(/^\/\d+/);
      })
      .bindComponent(this)
      .subscribe(cart => {
        loadTimeService.track(cart.contentId, cart.isCached, cart.config.producerId || null);

        // Goofy de renderização
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'rendered',
          data: {}
        });
      });

    // Goofy mounted
    cartService
      .getCart()
      .filter(cart => !cart.isCached)
      .first()
      .combineLatest(cartService.getConfig())
      .first()
      .bindComponent(this)
      .subscribe(([cart, config]) => {
        // Goofy de carregamento real do carrinho
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'mounted',
          data: {
            contentId: `${cart.contentId}`,
            coupon: (cart.coupon && cart.coupon.code) || null,
            couponValue: Number(cart.coupon && cart.coupon.value) || null,
            producerId: cart.config.producerId || null,
            affiliateId: cart.config.affiliateId || null,
            templateId: config.template.id,
            isOneClick: config.isOneClick,
            transactionKey: cart.transactionKey,
            isLaunch: cart.config.isLaunch,
            askAddress: !!(cart.config.askAddressBeforePayment || cart.config.askAddressAfterPayment),
            askAddressBeforePayment: !!cart.config.askAddressBeforePayment,
            askAddressAfterPayment: !!cart.config.askAddressAfterPayment,
            offerOrderBump: !!cart.orderBump
          }
        });
      });

    /*
      legacyPixelService: Should be removed in not too distance future
    */
    legacyPixelService.track(enLegacyPixelPath.CHECKOUT);

    pixelService.setPath(enPixelPath.CHECKOUT);

    this.observeState({ config: null }, cartService.getConfig());
    this.observeState({ cart: null }, cartService.getCart());

    // Scarcity Timer
    configService
      .getConfig()
      .take(1)
      .subscribe(config => this.setState({ hasTimer: !!config.timer }));
  }

  pay = () => {
    paymentService
      .getCart()
      .combineLatest(cartService.getCart())
      .first()
      .bindComponent(this)
      .subscribe(([cart, mountedCart]) => {
        ++attempts;

        const extraPaymentData = {};

        const payment = cart.payment[0];

        let method = 'CreditCard';
        if (payment.paymentMethod == enPaymentMethod.BOLETO) {
          method = 'Bankslip';
        }

        if (payment.paymentMethod == enPaymentMethod.ONE_CLICK_BUY) {
          method = 'OneClickBuy';
        }

        if (payment.paymentMethod == enPaymentMethod.PAYPAL) {
          method = 'Paypal';
        }

        if (payment.paymentMethod == enPaymentMethod.OTHER) {
          method = 'Other';
        }

        if (payment.paymentMethod == enPaymentMethod.FREE) {
          method = 'Free';
        }

        payAttempts[method] = payAttempts[method] || 0;
        payAttempts[method]++;

        extraPaymentData[`methodAttempt${method}`] = true;
        extraPaymentData[`attempt${attempts}`] = method;
        extraPaymentData['lastAttempt'] = method;
        extraPaymentData['brand'] = payment.brand || '';

        Object.keys(payAttempts).forEach(key => {
          extraPaymentData[`payAttempts${key}`] = payAttempts[key];
        });

        let extraUserData = {};

        if (cart.name) {
          extraUserData = {
            name: cart.name || '',
            email: cart.email || '',
            phone: cart.phone || ''
          };
        }

        let cartPrice = mountedCart.cartPrice;

        if (mountedCart.config.paymentType === enPaymentType.DONATION) {
          cartPrice = mountedCart.price;
        }

        // Goofy de envio de pagamento
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'payment_sent',
          data: {
            ...extraUserData,
            ...extraPaymentData,
            paymentMethod: paymentService.getNormalizedPaymentType(payment.paymentMethod),
            cartPrice,
            payAttempts: attempts,
            coupon: (cart.coupon && cart.coupon.code) || null,
            couponValue: Number(cart.coupon && cart.coupon.value) || null,
            installments: cart.payment[0].numberInstallments || 1,
            cards: cart.payment.length
          }
        });
      });

    return cartService
      .getCart()
      .filter(c => !c.isCached)
      .take(1)
      .switchMap(() => paymentService.pay())
      .retryWhen(errors$ => {
        return errors$.switchMap(err => {
          const errorCode = get(err, 'data.code', null);

          if (errorCode === 'CAP_A') {
            return captchaService.showCaptcha();
          }

          return Observable.throw(err);
        });
      })
      .logError()
      .catch(err => {
        const code: string = (err && err.data && err.data.code) || '';

        // Goofy de falha de pagamento
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'payment_failed',
          data: {}
        });

        const error = translateService.getErrorData(code);

        paymentProgressService.setPaymentProgressStatus({
          status: enPaymentStatus.ERROR,
          message: error.message || 'Não conseguimos nos comunicar com a operadora. Atualize a página ou tente novamente mais tarde.',
          code
        });

        return Observable.empty<IPaymentResult>();
      })
      .bindComponent(this)
      .combineLatest(paymentService.getCart())
      .do(([result, cart]) => {
        paymentProgressService.setPaymentProgressStatus({
          status: enPaymentStatus.SUCCESS
        });

        const isRedirectPayment = cart.payment[0].paymentMethod == enPaymentMethod.PAYPAL;

        if (isRedirectPayment) {
          // Goofy de redirect
          goofyService.track({
            flow: 'sale',
            trackerId: GOOFY_ID,
            step: 'payment_redirect',
            data: {
              amount: result.amount,
              paidAmount: result.paidAmount,
              paymentMethod: result.paymentMethod,
              paymentStatus: 1,
              personType: result.personType
            }
          });
        }

        if (!isRedirectPayment) {
          // Goofy de sucesso
          goofyService.track({
            flow: 'sale',
            trackerId: GOOFY_ID,
            step: 'payment_success',
            data: {
              amount: result.amount,
              paidAmount: result.paidAmount,
              paymentMethod: result.paymentMethod,
              paymentStatus: result.status,
              personType: result.personType,
              brand: result.brand
            }
          });
        }

        const cookie = new Cookies();

        cookie.remove(`goofy-${cart.contentId}`, {
          path: '/',
          domain: 'eduzz.com'
        });

        const oneMinute = 60;
        const oneHour = oneMinute * 60;
        const oneDay = oneHour * 24;

        cookie.set('last_purchase', `${result.paymentMethod}:${result.key}`, {
          maxAge: oneHour
        });
        if (result.he) {
          cookie.set('he', result.he, { maxAge: oneDay * 365 });
        }
        cookie.set('lastKey', result.key, { maxAge: oneHour });
        cookie.set('lastPaymentMethod', result.paymentMethod, {
          maxAge: oneHour
        });
        cookie.set('email', cart.email, { maxAge: oneDay * 365 });
      });
  };

  render() {
    const { loading, cart, config, hasTimer } = this.state;

    if (loading) {
      return null;
    }

    if (!cart) {
      return <Error />;
    }

    //  BaseComponent<IStateBase, IPayProps> }
    const templateMap: { [key: string]: any } = {
      14: HotsiteTheme,
      16: WidgetTheme,
      17: MobileTheme,
      18: MobileTheme,
      19: MobileTheme
    };

    // tslint:disable-next-line:variable-name
    const Component = templateMap[config.template.id] || templateMap[18];

    return (
      <HistoryContext.Provider value={this.props.history}>
        <div className={this.props.classes.component}>
          <Timer />
          <div className={`cart-container ${hasTimer ? 'cart-container-scarcity-timer' : ''}`}>
            <Helmet>
              <title>{cart.content.title}</title>
              <html className={'theme_' + config.template.id} />
            </Helmet>
            <Component onPay={this.pay} />
          </div>
        </div>
      </HistoryContext.Provider>
    );
  }
}
